// src/services/claudeAPI.js
/**
 * Claude API Service
 * Uses Cloudflare Worker proxy if configured, otherwise direct Anthropic.
 */
import axios from 'axios';
import { CLAUDE_CONFIG, ERROR_MESSAGES } from '@config/constants';

const isProxyUrl = (url) => typeof url === 'string' && /workers\.dev/i.test(url);
const joinPath = (base, path) =>
  `${String(base).replace(/\/+$/, '')}/${String(path).replace(/^\/+/, '')}`;

class ClaudeAPIService {
  constructor() {
    this.baseURL = CLAUDE_CONFIG.baseURL || '';
    this.apiKey  = CLAUDE_CONFIG.apiKey || '';
    this.model   = CLAUDE_CONFIG.model;

    this.useProxy = isProxyUrl(this.baseURL);

    const headers = {
      'Content-Type': 'application/json',
      'anthropic-version': '2023-06-01',
      // For direct calls only. Never ship API key from the browser to a proxy.
      ...(this.useProxy ? {} : { 'x-api-key': this.apiKey }),
    };

    this.client = axios.create({
      baseURL: this.baseURL.replace(/\/+$/, ''), // normalize
      headers,
      timeout: 60_000,
    });
  }

  isConfigured() {
    // Proxy mode needs only a reachable baseURL
    if (this.useProxy) return Boolean(this.baseURL);
    // Direct mode requires a real key
    return Boolean(this.apiKey && this.apiKey !== 'your_api_key_here');
  }

  // Small helper to normalize Anthropic/proxy response shapes
  static extractText(data) {
    if (!data) return null;
    // Standard Anthropic messages response
    if (Array.isArray(data.content) && data.content[0]?.text) {
      return data.content[0].text;
    }
    // Some proxies return { text }
    if (typeof data.text === 'string') return data.text;
    // Or { content: "..." }
    if (typeof data.content === 'string') return data.content;
    return null;
  }

  async sendMessage(prompt, options = {}) {
    if (!this.isConfigured()) {
      throw new Error(ERROR_MESSAGES.API_ERROR);
    }

    try {
      const url = joinPath(this.baseURL, '/v1/messages');
      const res = await this.client.post(url, {
        model: this.model,
        max_tokens: options.maxTokens || CLAUDE_CONFIG.maxTokens,
        temperature: options.temperature || CLAUDE_CONFIG.temperature,
        messages: [{ role: 'user', content: prompt }],
      });

      const text = ClaudeAPIService.extractText(res?.data);
      if (!text) throw new Error('Unexpected response from AI service.');
      return text;
    } catch (error) {
      // Surface precise, user-helpful messages
      if (error.response) {
        const { status, data } = error.response;
        const apiErr =
          data?.error?.message ||
          data?.error ||
          data?.message ||
          (typeof data === 'string' ? data : null) ||
          `HTTP ${status}`;

        if (!this.useProxy) {
          if (status === 401) throw new Error('Invalid API key (401).');
          if (status === 429) throw new Error('Rate limit exceeded (429). Try again later.');
        } else {
          // Proxy mode hints
          if (status === 401) throw new Error('Proxy could not use your CLAUDE_API_KEY (401).');
          if (status === 403) throw new Error('Proxy forbidden (403). Check Worker permissions/key.');
          if (status === 404) throw new Error('Proxy endpoint not found (404).');
        }
        throw new Error(`AI request failed: ${apiErr}`);
      }
      if (error.request) {
        throw new Error('Network/CORS error contacting proxy.');
      }
      throw new Error(error.message || ERROR_MESSAGES.API_ERROR);
    }
  }

  async analyzeResume(resumeText, jobDescription) {
    const prompt = `You are an expert resume analyzer and career coach. Analyze the following resume against the job description and provide detailed insights.

RESUME:
${resumeText}

JOB DESCRIPTION:
${jobDescription}

Return only valid JSON in this exact shape:
{
  "keywordAnalysis": { "matched": [], "missing": [], "relevanceScore": 0 },
  "atsScore": { "overall": 0, "breakdown": { "formatting": 0, "keywords": 0, "structure": 0, "contact": 0 } },
  "strengths": [],
  "weaknesses": [],
  "recommendations": [],
  "aiEnhancedSuggestions": []
}`;

    const raw = await this.sendMessage(prompt, {
      maxTokens: 4096,
      temperature: 0.3,
    });

    const cleaned = String(raw).trim()
      .replace(/^```json\s*/i, '')
      .replace(/^```\s*/i, '')
      .replace(/```$/i, '');

    try {
      return JSON.parse(cleaned);
    } catch {
      throw new Error('Failed to parse AI analysis JSON.');
    }
  }

  async enhanceBulletPoints(originalBullets, jobKeywords) {
    const prompt = `Enhance these resume bullets with keywords and impact.
ORIGINAL:
${originalBullets.join('\n')}
KEYWORDS: ${jobKeywords.join(', ')}

Return JSON array: [{"original":"","enhanced":"","keywords_added":[]}]`;

    const raw = await this.sendMessage(prompt, { temperature: 0.7 });
    const cleaned = String(raw).trim()
      .replace(/^```json\s*/i, '')
      .replace(/^```\s*/i, '')
      .replace(/```$/i, '');
    try {
      return JSON.parse(cleaned);
    } catch {
      return [];
    }
  }

  async generateSummary(resumeText, jobDescription) {
    const prompt = `Create a 3–4 sentence ATS-optimized professional summary that uses job keywords.

RESUME:
${resumeText.slice(0, 2000)}

JOB DESCRIPTION:
${jobDescription}`;
    return this.sendMessage(prompt, { maxTokens: 500, temperature: 0.7 });
  }
}

export const claudeAPI = new ClaudeAPIService();
export default ClaudeAPIService;
